/**
 * The only place the prejoin touches navigator.mediaDevices. Everything goes through the
 * {@link MediaDevicesLike} seam so tests inject a fake gateway instead of stubbing the global
 * navigator, and every entry point degrades to "nothing available" instead of throwing.
 */

import type { InputDeviceInfoLike } from './prejoinState';

/** Structural slice of Navigator.mediaDevices the prejoin needs. */
export interface MediaDevicesLike {
  enumerateDevices(): Promise<readonly InputDeviceInfoLike[]>;
  getUserMedia(constraints: MediaStreamConstraints): Promise<MediaStream>;
  addEventListener(type: 'devicechange', listener: () => void): void;
  removeEventListener(type: 'devicechange', listener: () => void): void;
}

/** Local preview streams: a video-only stream for the preview element, an audio stream for the meter. */
export interface PreviewStreams {
  readonly video: MediaStream | null;
  readonly mic: MediaStream | null;
}

const NO_STREAMS: PreviewStreams = { video: null, mic: null };

/** Returns navigator.mediaDevices when available, null otherwise (jsdom, insecure contexts). */
export function navigatorMediaDevices(): MediaDevicesLike | null {
  const devices = navigator.mediaDevices;
  if (devices === undefined) {
    return null;
  }
  return devices;
}

/** Enumerates devices without triggering a permission request; failures yield an empty list. */
export async function enumerateInputDevices(
  devices: MediaDevicesLike | null = navigatorMediaDevices(),
): Promise<readonly InputDeviceInfoLike[]> {
  if (devices === null) {
    return [];
  }
  try {
    return await devices.enumerateDevices();
  } catch {
    return [];
  }
}

function stopStream(stream: MediaStream): void {
  for (const track of stream.getTracks()) {
    track.stop();
  }
}

/**
 * Requests permission for one kind by opening and immediately stopping a default-device stream.
 * Resolves false when the device is missing or the user denies access.
 */
export async function requestDevicePermission(
  kind: 'video' | 'audio',
  devices: MediaDevicesLike | null = navigatorMediaDevices(),
): Promise<boolean> {
  if (devices === null) {
    return false;
  }
  const constraints: MediaStreamConstraints = kind === 'video' ? { video: true } : { audio: true };
  try {
    const stream = await devices.getUserMedia(constraints);
    stopStream(stream);
    return true;
  } catch {
    return false;
  }
}

/**
 * Opens the local preview for the selected devices. Camera and microphone are acquired
 * independently so a failing kind never takes the other one down; each failure degrades to null.
 * `audioConstraints` carries the microphone processing switches (echo cancellation etc.);
 * `videoConstraints` carries the session publish tier's resolution (the handed-over capture
 * must arrive at the dimensions the room publishes at); when omitted the browser defaults apply.
 */
export async function openPreview(
  cameraDeviceId: string | null,
  micDeviceId: string | null,
  devices: MediaDevicesLike | null = navigatorMediaDevices(),
  audioConstraints: MediaTrackConstraints = {},
  videoConstraints: MediaTrackConstraints = {},
): Promise<PreviewStreams> {
  if (devices === null) {
    return NO_STREAMS;
  }
  const video =
    cameraDeviceId !== null
      ? await acquireStream(devices, 'video', cameraDeviceId, videoConstraints)
      : null;
  const mic =
    micDeviceId !== null
      ? await acquireStream(devices, 'audio', micDeviceId, audioConstraints)
      : null;
  return { video, mic };
}

/** Acquires one kind with an exact deviceId and returns a single-kind stream, or null on failure. */
async function acquireStream(
  devices: MediaDevicesLike,
  kind: 'video' | 'audio',
  deviceId: string,
  extraConstraints: MediaTrackConstraints = {},
): Promise<MediaStream | null> {
  const constraints: MediaStreamConstraints =
    kind === 'video'
      ? { video: { deviceId: { exact: deviceId } } }
      : { audio: { deviceId: { exact: deviceId }, ...extraConstraints } };
  try {
    const stream = await devices.getUserMedia(constraints);
    const tracks = kind === 'video' ? stream.getVideoTracks() : stream.getAudioTracks();
    if (tracks.length === 0) {
      stopStream(stream);
      return null;
    }
    return new MediaStream(tracks);
  } catch {
    return null;
  }
}

/** Stops every track of a preview; safe to call repeatedly and on partial previews. */
export function stopPreview(preview: PreviewStreams | null): void {
  if (preview === null) {
    return;
  }
  if (preview.video !== null) {
    stopStream(preview.video);
  }
  if (preview.mic !== null) {
    stopStream(preview.mic);
  }
}

/** Subscribes to device hotplug events; returns an unsubscribe function (a no-op without devices). */
export function listenDeviceChange(
  listener: () => void,
  devices: MediaDevicesLike | null = navigatorMediaDevices(),
): () => void {
  if (devices === null) {
    return () => undefined;
  }
  try {
    devices.addEventListener('devicechange', listener);
    return () => {
      devices.removeEventListener('devicechange', listener);
    };
  } catch {
    return () => undefined;
  }
}
